<template>
    <div class="card">
        <DataTable
            selectionMode="single"
            :value="debriefs"
            :lazy="true"
            :resizableColumns="true"
            :paginator="true"
            :rows="10"
            ref="dt"
            dataKey="id"
            :totalRecords="totalRecords"
            :loading="loading"
            @page="onPage"
            @rowSelect="rowSelected"
            responsiveLayout="scroll"
            :rowsPerPageOptions="[5, 10, 20, 50, 100]"
            paginatorTemplate="CurrentPageReport JumpToPageInput FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries :: Page No:"
        >
            <template #empty> No debriefs available. </template>
            <template #loading> Loading debriefs. Please wait. </template>
            <Column field="incident_number" header="INCIDENT NO" style="width: 8rem" />
            <Column field="incident_category" header="INCIDENT/EXERCISE" style="max-width: 8rem" />
            <Column field="incident_date" header="INCIDENT DATE" style="max-width: 6rem" />
            <Column field="incident_type_name" header="INCIDENT TYPE" style="max-width: 6rem" />
            <Column field="appliance_name" header="CALL SIGN" style="max-width: 6rem" />
            <Column field="author_name" header="AUTHOR" style="max-width: 8rem" />
            <Column field="actions" header="ACTIONS" style="max-width: 6rem">
                <template #body="slotProps">
                    <span class="p-column-title">ACTIONS</span>
                    <Button v-if="UserPermission.WRITE_DEBRIEF" icon="pi pi-pencil" class="p-button-rounded p-button-success mr-2" @click="editDebrief(slotProps.data.id)" v-tooltip="'Edit Debrief'" />
                    <Button icon="pi pi-eye" class="p-button-rounded p-button-info" @click="viewDebrief(slotProps.data.id)" v-tooltip="'View Debrief'" />
                </template>
            </Column>
        </DataTable>
    </div>
</template>
<script setup>
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { reportStore, incidentStore } from '@/store/global-stores';
import { UserPermission } from '@/modules/permission/enum';
import { showToast } from '@/utilities/toast-handler';
import { formatDate } from '@/utilities/date-formatter';
import { getFullName, removeHtmlTagsFromParagraph } from '@/utilities';
import { getAllIncidentCategories } from '../../../incident/constants';

const emit = defineEmits(['table-loaded']);

const props = defineProps({
    criteriaSet: {
        type: Boolean,
    },
});

const router = useRouter();

const { params } = storeToRefs(reportStore);
const { flatIncidentTypes } = storeToRefs(incidentStore);

const incident_categories = getAllIncidentCategories();
const loading = ref(false);
const tableData = ref([]);
const totalRecords = ref(null);

// Computed properties
const debriefs = computed(() => {

    return tableData.value?.map((debrief) => {
        return {
            id: debrief.id,
            incident_number: debrief.incident?.incident_number,
            incident_category: incident_categories.find((incident_category) => incident_category.id == debrief.incident?.category)?.name,
            incident_date: formatDate(debrief.incident?.incident_date),
            incident_type_name: flatIncidentTypes.value?.find((item) => item.id == parseInt(debrief.incident?.incident_type_id))?.name ?? 'N/A',
            appliance_name: debrief.appliance?.name,
            author_name: getFullName(debrief.author),
            learning_outcomes: debrief.learning_outcome ? debrief.learning_outcome.map((learning, index) => `LO${index + 1}:` + (learning?.details ? removeHtmlTagsFromParagraph(learning?.details) : 'No Description found')) : [],
        };
    });
});

watch(
    () => props.criteriaSet,
    () => loadDebriefReportTableData()
);

onMounted(() => {
    loadDebriefReportTableData();
});

onUnmounted(() => {
    reportStore.initparams();
});

// Actions
const loadDebriefReportTableData = async () => {
    // load the table data from here
    try {
        loading.value = true;
        // Setting to graphs to load graph data
        const response = await reportStore.getDynamicDebriefReportData('template_tabular');
        // Format the table
        tableData.value = response.debriefs.data;
        totalRecords.value = response.debriefs.total;
        // This record is emitted to load the result bar in the DebriefReports
        emit('table-loaded', totalRecords.value);
    } catch (error) {
        showToast({}, error?.data?.message ?? 'Failed to debrief report table');
    } finally {
        loading.value = false;
    }
};

const onPage = async (event) => {
    params.value = event;
    await loadDebriefReportTableData();
};

const editDebrief = (id) => {
    router.push({ name: 'DebriefCreateUpdate', query: { debriefId: id } });
};

// Redirect to page in case a row have been selected
const rowSelected = (event) => {
    viewDebrief(event.data.id);
};

const viewDebrief = (id) => {
    router.push({ name: 'DebriefDetails', params: { id } });
};
</script>
